// src/hooks/useTenant.ts
import { useQuery } from '@tanstack/react-query';
import { getTenantByIdRequest } from '@/api';
import { useAuth } from '@/context/AuthContext';
import { IUser,  IProperty, TenantLease } from '@/types/entities';
import { IUnit } from '@/types/units';

// Define the type for the full API response
interface TenantDataResponse {
  tenant: IUser;
  leases: TenantLease[];
}

// Define the type for the data returned by the hook for component consumption
interface UseTenantData {
  tenant: IUser;
  leases: TenantLease[];
  // For backward compatibility - providing the first lease and its details
  lease?: TenantLease;
  unit?: IUnit;
  property?: IProperty;
}

function useTenant(tenantId: string | number | null | undefined) {
  const { isAuthenticated } = useAuth();

  return useQuery<TenantDataResponse, Error, UseTenantData>({
    queryKey: ['tenant', tenantId],
    queryFn: async () => {
      if (!tenantId) throw new Error('Tenant ID is required');
      const response = await getTenantByIdRequest(tenantId);
      return response.data;
    },
    select: (data) => {
      // Find the first active lease, or just the first lease if none are active
      const firstLease =
        data.leases?.find((l) => l.status === 'active') ||
        (data.leases && data.leases.length > 0 ? data.leases[0] : undefined);

      const tenantWithPhone = {
        ...data.tenant,
        phone: data.tenant.personal?.personal_phone || data.tenant.phone || '',
      };

      return {
        tenant: tenantWithPhone,
        leases: data.leases || [],
        // For backward compatibility
        lease: firstLease,
        unit: firstLease?.unit,
        property: firstLease?.unit?.property,
      };
    },
    enabled: !!isAuthenticated && !!tenantId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnMount: true,
    refetchOnWindowFocus: false,
  });
}

export default useTenant;
